/*
 * ------------------------------------------------------------------------------------------
 * - External Imports -
 * ------------------------------------------------------------------------------------------
 */
import { OnlinePatientManagementProvider } from '@ngt/opms';
import * as Sentry from "@sentry/react";
import { JsonServiceClient } from '@servicestack/client';
import { createRoot } from 'react-dom/client';

/*
 * ------------------------------------------------------------------------------------------
 * - Internal Imports -
 * ------------------------------------------------------------------------------------------
 */

import { FC, StrictMode, useCallback, useEffect } from 'react';
import { EventDefinitionCollectionResponse, FormDefinitionCollectionResponse, GetCachedEventDefinitions, GetCachedFormDefinitions, GetCachedPatientStates, GetCachedTreatments, GetFormMetadata, GetFormMetadataResponse, GetUser, GetUserResponse, IReturn, PatientStateCollectionResponse, TreatmentCollectionResponse } from './api/dtos';
import { AppSettings } from './AppSettings';
import createOnlinePatientManagement from './opms/onlinePatientManagement';


import { LocalizationProvider } from '@mui/x-date-pickers';
import { createBrowserRouter, createRoutesFromChildren, matchRoutes, RouteObject, useLocation, useNavigationType } from 'react-router-dom';
import App from './App';
import './i18n';
import RouteErrorBoundary from './components/errors/RouteErrorBoundary';

/*
 * ------------------------------------------------------------------------------------------
 * - Render -
 * ------------------------------------------------------------------------------------------
 */

declare let global: any;

if (typeof global === 'undefined') {
    (window as any).global = window;
}

if (AppSettings.sentry.dsn) {
    const integrations = [
        // React Router V6 integration.
        // Helps with tracing URL changes and which URLs belong to the 
        // same route, for example:
        //    - https://test.com/form/1
        //    - https://test.com/form/2
        // both belong to the same route */form/*
        Sentry.reactRouterV7BrowserTracingIntegration({
            useEffect: useEffect,
            useLocation,
            useNavigationType,
            createRoutesFromChildren,
            matchRoutes,
        }),

        // Browser Tracing integration
        // Helps with tracing URLs and other common browser "spans"
        Sentry.browserTracingIntegration(),

        // Replay integration
        // Records user actions and sends to Sentry.
        Sentry.replayIntegration({
            // Whether to mask all text on the page when recording user actions
            // When false only inputs are masked.
            // There is a separate setting to force unmasked inputs.
            maskAllText: false,

            // Whether to mask all images on the page when recording user 
            // actions.
            blockAllMedia: false,

            // Which URLs can the replay run on.
            // URL must contain one of the strings in the array or match one of
            // the regexes supplied
            networkDetailAllowUrls: [window.location.origin],

            // Capture the body of network requests
            // Sentry will still run data scrubbing on the bodies.
            networkCaptureBodies: true
        })
    ];

    if (AppSettings.sentry.enableReportABug) {
        integrations.push(Sentry.feedbackIntegration({
            // Disable the injection of the default widget
            autoInject: false,
            colorScheme: 'system',
            showBranding: false,
            showName: false,
            showEmail: false,
            tags: {
                Application: AppSettings.sentry.application,
                Customer: AppSettings.sentry.customer
            },
            useSentryUser: {
                name: 'fullName',
                email: 'email',
            },
        }));
    }

    Sentry.init({
        // The DSN to use when sending information to Sentry.
        // Identifies a specific project in Sentry.
        dsn: AppSettings.sentry.dsn,

        // Whether to send identifying information to Sentry such as
        // username, useragent, ip, etc
        sendDefaultPii: AppSettings.sentry.sendDefaultPii,

        // Whether to sent available performance metrics to Sentry.
        // These are the basic performance metrics and not the endepth
        // profiling metrics that take a lot more effort to setup.
        enableMetrics: AppSettings.sentry.enableMetrics,

        // Whether to attach stacktraces to actions sent to Sentry.
        attachStacktrace: AppSettings.sentry.attachStacktrace,

        // Whether @sentry/react is in debug mode, logging more information
        // to the console.
        debug: AppSettings.sentry.debug,

        // initialise integrations.
        integrations: integrations,

        // The environment to associate all action sent to Sentry with.
        environment: AppSettings.sentry.environment,

        // Release to associate all actions sent to Sentry with.
        release: AppSettings.sentry.release,

        // Percentage of captured traces that are sent to Sentry.
        // 1.0 = 100%, 0 = 0%
        tracesSampleRate: AppSettings.sentry.tracesSampleRate,

        // Which URLs to trace / record in a trace.
        // Only trace internal URLs don't want to trace every SEO url
        // under the sun.
        tracePropagationTargets: [
            'localhost', 
            /^\//,
            new RegExp(`/^${window.location.origin}`)
        ],

        // Percentage of captured session replays that are sent to Sentry.
        // 1.0 = 100%, 0 = 0%
        replaysSessionSampleRate: AppSettings.sentry.replaysSessionSampleRate,

        // Percentage of captured session replays that are sent to Sentry.
        // when an error occurs
        // 1.0 = 100%, 0 = 0%
        replaysOnErrorSampleRate: AppSettings.sentry.replaysOnErrorSampleRate,

        // Enable logs to be sent to Sentry.
        enableLogs: AppSettings.sentry.enableLogs,
    });
    Sentry.setTag('Application', AppSettings.sentry.application);
    Sentry.setTag('Customer', AppSettings.sentry.customer);
}

interface IAppContentProps {
    user?: GetUserResponse, 
    formMetadata?: GetFormMetadataResponse,
    eventDefinitions?: EventDefinitionCollectionResponse, 
    formDefinitions?: FormDefinitionCollectionResponse, 
    patientStates?: PatientStateCollectionResponse, 
    treatments?: TreatmentCollectionResponse
}

const AppContent: FC<IAppContentProps> = ({
    user,
    formMetadata,
    eventDefinitions,
    formDefinitions,
    patientStates,
    treatments 
}) => {    
    const onlinePatientManagement = createOnlinePatientManagement(user, formMetadata, eventDefinitions, formDefinitions, patientStates, treatments);

    if (!onlinePatientManagement) {
        return null;
    }

    const createRouter = useCallback((routes: RouteObject[]) => Sentry.wrapCreateBrowserRouterV7(createBrowserRouter)(routes), [createBrowserRouter]);

    return (
        <LocalizationProvider
            localeText={{fieldMonthPlaceholder: () => "MMM"}}
        >
            <OnlinePatientManagementProvider 
                value={onlinePatientManagement}
                configuration={{
                    authenticatedUser: {
                        revalidateOnMount: false
                    },
                    eventDefinitions: {
                        revalidateOnMount: false
                    },
                    formDefinitions: {
                        revalidateOnMount: false
                    },
                    formMetadata: {
                        revalidateOnMount: false
                    },
                    patientStates: {
                        revalidateOnMount: false
                    },
                    treatments: {
                        revalidateOnMount: false
                    },
                }}
                createRouter={createRouter}
                ErrorBoundary={RouteErrorBoundary}
            >
                <App />
            </OnlinePatientManagementProvider>
        </LocalizationProvider>
    );
}

const loadData = async <T,>(client: JsonServiceClient, request: IReturn<T>) => {
    try {
        return await client.get(request);
    }
    catch (error) {
        console.error(error);
    }

    return undefined;
}

const bootstrap = async () => {
    if (AppSettings.debug) {
        const client = new JsonServiceClient();

        const [
            user, 
            formMetadata, 
            eventDefinitions,
            formDefinitions,
            patientStates,
            treatments
        ] = await Promise.all([
            loadData(client, new GetUser()),
            loadData(client, new GetFormMetadata()),
            loadData(client, new GetCachedEventDefinitions()),
            loadData(client, new GetCachedFormDefinitions()),
            loadData(client, new GetCachedPatientStates()),
            loadData(client, new GetCachedTreatments()),
        ]);

        return (
            <AppContent 
                user={user} 
                formMetadata={formMetadata}
                eventDefinitions={eventDefinitions}
                formDefinitions={formDefinitions}
                patientStates={patientStates}
                treatments={treatments}
            />
        );
    } else {
        const user = global.AUTH as GetUserResponse | undefined;
        const formMetadata = global.FORM_METADATA as GetFormMetadataResponse | undefined;
        const eventDefinitions = (global as any).EVENT_DEFINITIONS as EventDefinitionCollectionResponse | undefined;
        const formDefinitions = (global as any).FORM_DEFINITIONS as FormDefinitionCollectionResponse | undefined;
        const patientStates = (global as any).PATIENT_STATES as PatientStateCollectionResponse | undefined;
        const treatments = (global as any).TREATMENTS as TreatmentCollectionResponse | undefined;

        return (
            <AppContent 
                user={user} 
                formMetadata={formMetadata}
                eventDefinitions={eventDefinitions}
                formDefinitions={formDefinitions}
                patientStates={patientStates}
                treatments={treatments}
            />
        );
    }
}

document.addEventListener('DOMContentLoaded', async function(_event) { 
    const appContent = await bootstrap();

    const root = AppSettings.sentry.dsn ? 
        createRoot(
            document.getElementById('root')!,
            {
                // Callback called when React automatically recovers from errors.
                onRecoverableError: Sentry.reactErrorHandler(),
            }
        ) :
        createRoot(document.getElementById('root')!);

    root.render(
        <StrictMode>
            <Sentry.ErrorBoundary fallback={<p>An error has occurred</p>}>
                {appContent}
            </Sentry.ErrorBoundary>
        </StrictMode>
    )
});